/* eslint-disable @typescript-eslint/naming-convention */
import DateTextField from '@brands/Components/Inputs/MuiDateTextField/DateTextField';
import MultiSelectInputMUI from '@brands/Components/Inputs/MuiMultiSelectInput/MultiSelectInputMUI';
import MUISelectInput from '@brands/Components/Inputs/MuiSelectInput/MUISelectInput';
import PhoneInput from '@brands/Components/Inputs/MuiTelInput/MuiTelInput';
import TextField from '@brands/Components/Inputs/MuiTextField/TextField';
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import ProfileImage from '@brands/Components/ProfileImage/ProfileImage';
import UnsavedChangesModal from '@brands/Dashboard/Dashboard/Components/UnsavedChangesModal/UnsavedChangesModal';
import ReadOnlyDependent from '@brands/Dashboard/MyProfile/Patient/Sections/Dependent/ReadOnlyDependent';
import ReadOnlyOrganizations from '@brands/Dashboard/MyProfile/Patient/Sections/Organizations/ReadOnlyOrganizations';
import ValidateAddressModal from '@brands/Dashboard/MyProfile/Patient/Sections/ValidateAddressModal/ValidateAddressModal';
import { MyProfileSubmit } from '@brands/Dashboard/MyProfile/Patient/utils/types';
import ChangePasswordModal from '@brands/Dashboard/Sidenav/SidenavModals/ChangePasswordModal/ChangePasswordModal';
import { useAppDispatch, useAppSelector } from '@brands/hooks/useReduxHook';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { useSnackbar } from '@brands/hooks/useStackbar';
import { useUnsavedChangesWarning } from '@brands/hooks/useUnsavedChangesWarning';
import { RegisterMediaPayload } from '@brands/services/communication/registersConversationMedia';
import { confirmAvatarMedia } from '@brands/services/identity/confirmAvatarMedia';
import { createBasicInformation } from '@brands/services/identity/createBasicInformation';
import { deleteAvatar } from '@brands/services/identity/deleteAvatar';
import { getCurrentOrganization } from '@brands/services/identity/getCurrentOrganization';
import { getMyOrganizations } from '@brands/services/identity/getMyOrganizations';
import { listAllLanguages } from '@brands/services/identity/listAllLanguages';
import { registerAvatarMedia } from '@brands/services/identity/registerAvatarMedia';
import { saveBasicInformation } from '@brands/services/identity/saveBasicInformation';
import {
  Address,
  BasicInfo,
  Language,
  Organization,
  UserProfile,
  UserRoleName,
} from '@brands/services/identity/types/UserProfile';
import { UpdateMe, updateMe } from '@brands/services/identity/updateMe';
import { mapProfileToNewMyProfileSubmit } from '@brands/store/actions/patientAction';
import { selectAuth } from '@brands/store/selectors/auth';
import { setUser } from '@brands/store/slices/authSlice';
import { setPatientMyProfile } from '@brands/store/slices/patientMyProfileSlice';
import { displayErrorDetails } from '@brands/Utils/displayError';
import GenericSnackbar from '@brands/Utils/GenericSnackbar';
import { genderIdentity, genderOptions, pronounsOptions } from '@brands/Utils/selectButtonOptions';
import { OptionLanguage, stateOptions } from '@brands/Utils/selectOptions';
import { UnifiedProfileSubmit } from '@brands/Utils/types';
import { validateAddress, ValidationModalInterface } from '@brands/Utils/validateAddress';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@mui/material';
import dayjs from 'dayjs';
import { E164Number, parsePhoneNumber } from 'libphonenumber-js';
import { isEqual } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm, UseFormGetValues, useWatch } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { UserCompleteProfileForm } from 'src/brands/dentistry-one/Auth/SignUp/types';

import { updateData } from '../../utils/utilsFunctions';
import EditDependent from '../Dependent/EditDependent';
import styles from './styles.module.scss';
import { profileValidationSchema } from './validationSchema';

const Profile = ({
  isProfileCard,
  formRef,
  setIsDirty,
  isEditDependent,
  setIsEditDependent,
  dependentId,
  setDependentId,
}: {
  isProfileCard: boolean;
  formRef: React.RefObject<HTMLFormElement>;
  setIsDirty: (value: boolean) => void;
  isEditDependent: boolean;
  setIsEditDependent: React.Dispatch<React.SetStateAction<boolean>>;
  dependentId: number | undefined;
  setDependentId: React.Dispatch<React.SetStateAction<number | undefined>>;
}): JSX.Element => {
  const dispatch = useAppDispatch();
  const { userInfo } = useAppSelector(selectAuth);
  const { state } = useLocation();
  const [resetPasswordModal, setResetPasswordModal] = useState(false);
  const [currentAvatarURL, setCurrentAvatarURL] = useState<string>('');
  const roleNavigate = useRoleNavigate();
  const [myProfileForm, setMyProfileForm] = useState<UserCompleteProfileForm>({} as UserCompleteProfileForm);
  const [image, setImage] = React.useState<string>();
  const [avatarPayload, setAvatarPayload] = useState<RegisterMediaPayload | null>(null);
  const [thisUser, setThisUser] = useState<UserCompleteProfileForm>();
  const [avatarAction, setAvatarAction] = useState<string>('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [validationResponseId, setValidationResponseId] = useState('');
  const [loading, setLoading] = useState(true);
  const [languageOptions, setLanguageOptions] = useState<OptionLanguage[]>([]);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isReturnToSelectPatient, setReturnToSelectPatient] = useState(false);
  const [showDeleteDependentConfirmationModal, setShowDeleteDependentConfirmationModal] = useState(false);
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [showValidationAddressModal, setShowValidationAddressModal] = useState<ValidationModalInterface>({
    isOpen: false,
    title: '',
    whatYouEntered: '',
    recommended: false,
    recommendedAddress: '',
    onClose: undefined,
    onConfirmChange: undefined,
    onKeepCurrentAddress: undefined,
  });

  const onEditDependent = (id: number): void => {
    setDependentId(id);
    setIsEditDependent(true);
  };

  const addDependent = (): void => {
    setDependentId(undefined);
    setIsEditDependent(true);
  };

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    formState: { errors },
    getValues,
    control,
  } = useForm<UserCompleteProfileForm>({
    mode: 'onChange',
    resolver: yupResolver(profileValidationSchema()),
    defaultValues: myProfileForm,
  });

  const fetchLanguageOptions = async (): Promise<void> => {
    const data = await listAllLanguages();
    const transformedData = data.map((language) => ({
      value: language.locale,
      label: language.name,
      id: language.id,
    }));
    setLanguageOptions(transformedData);
  };

  const watchedValues = useWatch({ control });

  useEffect(() => {
    fetchLanguageOptions();
  }, []);

  const fetchProfile = (): void => {
    const mappedProfile = mapProfileToNewMyProfileSubmit(userInfo);

    setMyProfileForm(mappedProfile);
    setThisUser(mappedProfile as UserCompleteProfileForm);

    function isLanguageArray(value: Language[] | string[]): value is Language[] {
      return Array.isArray(value) && value.length > 0 && value[0] instanceof Object && 'id' in value[0];
    }
    let selectedLanguage: string[] = [];
    if (isLanguageArray(mappedProfile.languages)) {
      selectedLanguage = mappedProfile.languages.map((mappedLanguage) => mappedLanguage.id.toString());
    }

    dispatch(
      setPatientMyProfile({
        ...(mappedProfile as MyProfileSubmit),
        language: selectedLanguage,
        profile_img: currentAvatarURL,
      }),
    );
    setMyProfileForm({
      ...(mappedProfile as UserCompleteProfileForm),
      language: selectedLanguage,
      profile_img: currentAvatarURL,
    });
  };

  useEffect(() => {
    fetchProfile();
    setLoading(false);
  }, [userInfo, languageOptions]);


  useEffect(() => {
    setMyProfileForm((prev) => (Object.keys(prev).length > 0 ? { ...prev, profile_img: currentAvatarURL } : prev));
  }, [currentAvatarURL]);

  useEffect(() => {
    if (Object.keys(myProfileForm).length > 0) {
      setTimeout(() => {
        Object.keys(myProfileForm).forEach((k: unknown) => {
          const value = myProfileForm[k as keyof UserCompleteProfileForm];
          setValue(k as keyof UserCompleteProfileForm, value as any);
        });
      }, 0);
    }
  }, [myProfileForm]);

  const triggerProfileAvatar = React.useCallback(
    (avatarUrl: string) => {
      setValue('profile_img', avatarUrl, { shouldValidate: true });
    },
    [getValues, setValue],
  );

  const onInvalid = (err: any): void => {
    const error = Object.values(err)[0] as any;
    if (error) {
      displayErrorDetails(error);
    }
  };

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);

  const fetchOrganizations = useCallback(async () => {
    try {
      const orgs = await getMyOrganizations();
      setOrganizations(orgs);
      const currentOrg = await getCurrentOrganization();
      setCurrentOrganization(currentOrg);
      setSelectedOrgId(currentOrg?.id.toString() ?? '');
    } catch (err) {
      displayErrorDetails(err);
    }
  }, []);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  useEffect(() => {
    const anyChange = !isEqual(myProfileForm, watchedValues) || avatarAction !== '';

    setIsDirty?.(anyChange);
  }, [watchedValues, avatarAction, myProfileForm]);

  const switchOrganization = (organization: Organization): void => {
    const { id: organizationId, divisions, practices } = organization;
    let currentPath = window.location.pathname;
    let newURL = '';

    // Remove role segment if it matches one of the UserRoleName values
    const segments = currentPath.split('/');
    if (segments.length > 1) {
      const roleNames = Object.values(UserRoleName);
      if (roleNames.includes(segments[1] as UserRoleName)) {
        segments.splice(1, 1);
        currentPath = segments.join('/') || '/';
      }
    }

    if (organizationId && divisions.length > 0) {
      newURL = `https://${divisions[0].domain}${currentPath}`;
    } else if (organizationId && practices.length > 0) {
      newURL = `https://${practices[0].domain}${currentPath}`;
    }

    if (newURL) {
      window.location.href = newURL;
    }
  };

  const callSaveProfile = async (profileData: UnifiedProfileSubmit): Promise<void> => {
    setIsSavingProfile(true);
    setLoading(true);
    let updatedUser;
    try {
      if (avatarPayload && avatarFile && avatarAction === 'Upload') {
        const res = await registerAvatarMedia(avatarPayload);
        const mediaId = res.files[0].upload_id;
        await fetch(res.files[0].upload_url, {
          method: 'PUT',
          body: avatarFile,
          headers: { 'content-type': avatarFile.type },
        });
        await confirmAvatarMedia(mediaId, Number(thisUser?.id));
        triggerProfileAvatar(res.files[0].upload_url);
      } else if (avatarAction === 'Remove') {
        await deleteAvatar(thisUser?.id || 0);
        triggerProfileAvatar('');
      }

      const dataWithUpdatedDOB = updateData(profileData);
      const { address1, address2, city, state, zip_code } = profileData;
      const userAddress: Address = { address1, address2, city, state, zip_code };
      updatedUser = await updateMe(
        {
          ...(profileData as UpdateMe),
          phone_number: parsePhoneNumber((profileData as UserCompleteProfileForm).phone_number, 'US')?.format(
            'E.164',
          ) as string,
          address: userAddress as Address,
        },
        thisUser?.id || 0,
      );
      if (thisUser && thisUser?.first_name !== null) {
        updatedUser = await saveBasicInformation(
          {
            ...(dataWithUpdatedDOB as BasicInfo),
            languages: (profileData as UserCompleteProfileForm).language as string[],
          },
          thisUser?.id || 0,
        );
      } else {
        updatedUser = await createBasicInformation(
          {
            ...(dataWithUpdatedDOB as BasicInfo),
            languages: (profileData as UserCompleteProfileForm).language as string[],
          },
          thisUser?.id || 0,
        );
      }

      setAvatarAction('');
      dispatch(setUser({ userInfo: updatedUser }));
      const selectedOrganization = organizations.find((o) => o.id.toString() === selectedOrgId);
      if (selectedOrganization?.id !== currentOrganization?.id && selectedOrgId !== '') {
        switchOrganization(selectedOrganization as Organization);
      }
      showSnackbar('Your changes have been saved', 'success');
    } catch (error: unknown) {
      displayErrorDetails(error);
    } finally {
      setIsSavingProfile(false);
      setLoading(false);
      if (state?.needToCompleteProfile) {
        setReturnToSelectPatient(true);
      }
    }
  };

  const onCloseConfirmationModal = (): void => {
    setShowValidationAddressModal({
      isOpen: false,
      title: '',
      whatYouEntered: '',
      recommended: false,
      recommendedAddress: '',
      onClose: undefined,
      onConfirmChange: undefined,
      onKeepCurrentAddress: undefined,
    });
  };

  const updateCurrentAddress = (standardizedAddress: any, postalAddress: any, addressComponents: any): void => {
    const streetNumber = addressComponents.find(
      (addressComponent: any) => addressComponent.componentType === 'street_number',
    );
    const address1 = addressComponents.find((addressComponent: any) => addressComponent.componentType === 'route');
    const state = addressComponents.find(
      (addressComponent: any) => addressComponent.componentType === 'administrative_area_level_1',
    );

    if (postalAddress) {
      setValue('city', postalAddress.locality);
      setValue('zip_code', postalAddress.postalCode);
    }

    if (streetNumber || address1) {
      setValue('address1', `${streetNumber?.componentName?.text || ''} ${address1?.componentName?.text || ''}`.trim());
    }

    if (state) {
      setValue('state', state.componentName?.text || '');
    } else if (standardizedAddress?.state) {
      setValue('state', standardizedAddress.state);
    }
  };

  const onSubmitProfile = async (profileData: UserCompleteProfileForm): Promise<void> => {
    await validateAddress(
      profileData,
      getValues as UseFormGetValues<UnifiedProfileSubmit>,
      validationResponseId,
      callSaveProfile,
      setValidationResponseId,
      setShowValidationAddressModal,
      onCloseConfirmationModal,
      updateCurrentAddress,
    );
  };

  const renderPrimaryAccount = (): JSX.Element => {
    return (
      <div className={styles.ProfileDiv}>
        <div className={styles.profileAvatar}>
          <ProfileImage
            reviewProfile={false}
            id={myProfileForm.id?.toString()}
            setCurrentAvatarURL={setCurrentAvatarURL}
            setAvatarFile={setAvatarFile}
            setAvatarPayload={setAvatarPayload}
            setAvatarAction={setAvatarAction}
            triggerProfileAvatar={triggerProfileAvatar}
            setImage={setImage}
            image={image}
            errors={errors}
            size="80px"
          />
        </div>
        <form ref={formRef} className={styles.formContainer} onSubmit={handleSubmit(onSubmitProfile, onInvalid)}>
          <div className={styles.inputWrapRow}>
            <div className={styles.inputWrap}>
              <TextField
                {...register('first_name')}
                label="First name"
                data-testid="profile-first-name"
                errors={errors.first_name ? errors : {}}
                name="first_name"
                value={watch('first_name')}
                onChange={(e: any) => {
                  if (errors.first_name) clearErrors('first_name');
                  setValue('first_name', e.target.value, { shouldValidate: true });
                }}
                required
                inputClass={`${styles.profileInputClass} `}
                wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
              />
            </div>
            <div className={styles.inputWrap}>
              <TextField
                {...register('middle_name')}
                label="Middle name (optional)"
                data-testid="profile-middle-name"
                errors={errors.middle_name ? errors : {}}
                name="middle_name"
                value={watch('middle_name')}
                onChange={(e: any) => {
                  if (errors.middle_name) clearErrors('middle_name');
                  setValue('middle_name', e.target.value, { shouldValidate: true });
                }}
                inputClass={`${styles.profileInputClass} `}
                wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
              />
            </div>
            <div className={styles.inputWrap}>
              <TextField
                {...register('last_name')}
                label="Last name"
                data-testid="profile-last-name"
                errors={errors.last_name ? errors : {}}
                name="last_name"
                value={watch('last_name')}
                onChange={(e: any) => {
                  if (errors.last_name) clearErrors('last_name');
                  setValue('last_name', e.target.value, { shouldValidate: true });
                }}
                required
                inputClass={`${styles.profileInputClass} `}
                wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
              />
            </div>
          </div>
          <div className={styles.inputWrapRow}>
            <div className={styles.inputWrap}>
              <TextField
                {...register('preferred_name')}
                label="Preferred name (optional)"
                errors={errors.preferred_name ? errors : {}}
                name="preferred_name"
                value={watch('preferred_name')}
                onChange={(e: any) => {
                  if (errors.preferred_name) clearErrors('preferred_name');
                  setValue('preferred_name', e.target.value, { shouldValidate: true });
                }}
                inputClass={`${styles.profileInputClass} `}
                wrapperClassName={`fs-unmask ${styles.createAccountPreferredName}`}
              />
            </div>
            <div className={styles.inputWrap}>
              <DateTextField
                {...register('date_of_birth')}
                label="Date of birth"
                data-testid="profile-date-of-birth"
                value={watch('date_of_birth') ? dayjs(watch('date_of_birth')) : null}
                errors={errors.date_of_birth ? errors : {}}
                name="date_of_birth"
                onChange={(newValue: any) => {
                  if (errors.date_of_birth) clearErrors('date_of_birth');

                  let formattedDate = '';
                  if (newValue) {
                    formattedDate = dayjs(newValue).format('MM/DD/YYYY');
                  }

                  setValue('date_of_birth', formattedDate, { shouldValidate: true });
                }}
                required
                inputClass={`${styles.profileInputClass} `}
                wrapperClassName={`fs-unmask ${styles.completeProfileDOB}`}
              />
            </div>
          </div>
          <div className={styles.inputWrapRow}>
            <div className={styles.inputWrap}>
              <MUISelectInput
                {...register('gender')}
                label="Gender assigned at birth"
                data-testid="profile-gender"
                errors={errors.gender ? errors : {}}
                value={getValues('gender') || ''}
                name="gender"
                onChange={(e: any) => {
                  if (errors.gender) clearErrors('gender');
                  setValue('gender', e, { shouldValidate: true });
                }}
                required
                inputClass={`${styles.profileInputClass} `}
                wrapperClassName={`fs-unmask ${styles.completeProfileClassGender}`}
                options={genderOptions}
              />
            </div>
            <div className={styles.inputWrap}>
              <MUISelectInput
                {...register('gender_identity')}
                label="Gender identity"
                data-testid="profile-gender-identity"
                value={watch('gender_identity') || ''}
                errors={errors.gender_identity ? errors : {}}
                name="gender_identity"
                onChange={(e: any) => {
                  if (errors.gender_identity) clearErrors('gender_identity');
                  setValue('gender_identity', e);
                }}
                required
                inputClass={`${styles.profileInputClass} `}
                wrapperClassName={`fs-unmask ${styles.completeProfileClassIdentity}`}
                options={genderIdentity}
              />
            </div>
            <div className={styles.inputWrap}>
              <MUISelectInput
                {...register('pronouns')}
                label="Preferred pronouns (optional)"
                data-testid="profile-pronouns"
                errors={errors.pronouns ? errors : {}}
                value={watch('pronouns') || ''}
                name="pronouns"
                onChange={(e: any) => {
                  if (errors.pronouns) clearErrors('pronouns');
                  setValue('pronouns', e);
                }}
                inputClass={`${styles.profileInputClass} `}
                wrapperClassName={`fs-unmask ${styles.completeProfileClassPronouns}`}
                options={pronounsOptions}
              />
            </div>
          </div>
          <div className={styles.inputWrapRow}>
            <div className={styles.inputWrap}>
              <MultiSelectInputMUI
                {...register('language')}
                label="Languages"
                data-testid="profile-language"
                errors={errors.language ? errors : {}}
                name="language"
                onChange={(selectedValues: OptionLanguage[]) => {
                  if (errors.language) clearErrors('language');
                  setValue('language', selectedValues, { shouldValidate: true });
                }}
                withId
                value={
                  Array.isArray(watch('language')) && typeof watch('language')[0] === 'string'
                    ? (watch('language') as string[])
                    : Array.isArray(watch('language')) && typeof watch('language')[0] === 'object'
                      ? ((watch('language') as OptionLanguage[]).map((lang: OptionLanguage) =>
                          lang.id?.toString(),
                        ) as string[])
                      : []
                }
                required
                inputClass={`${styles.profileInputClass} `}
                wrapperClassName={`fs-unmask ${styles.completeProfileLanguage}`}
                options={languageOptions}
              />
            </div>
            <div className={`${styles.inputWrap} ${styles.inputWrapPhoneInput}`}>
              <PhoneInput
                {...register('phone_number')}
                value={watch('phone_number') as E164Number}
                data-testid="profile-phone-number"
                id="phone_number"
                name="phone_number"
                onChange={(val: string | undefined) => {
                  if (val === undefined || val === '') {
                    setValue('phone_number', '', { shouldValidate: true });
                    return;
                  }

                  setValue('phone_number', val, { shouldValidate: true }); // Store in E.164 format
                }}
                required
                label="Phone number"
                errors={errors}
                inputClass={`${styles.profileInputClass} `}
                wrapperClassName={`fs-unmask ${styles.completeProfilePhone}`}
              />
            </div>
          </div>
          <div className={styles.inputWrapRow}>
            <div className={styles.inputWrap}>
              <TextField
                {...register('address1')}
                label="Address"
                data-testid="profile-address1"
                errors={errors.address1 ? errors : {}}
                name="address1"
                value={watch('address1')}
                onChange={(e: any) => {
                  if (errors.address1) clearErrors('address1');
                  setValue('address1', e.target.value, { shouldValidate: true });
                }}
                required
                inputClass={`${styles.profileInputClass} `}
                wrapperClassName={`fs-unmask ${styles.completeProfileAddress}`}
              />
            </div>
            <div className={styles.inputWrap}>
              <TextField
                {...register('address2')}
                label="Apartment, suite, etc. (optional)"
                errors={errors.address2 ? errors : {}}
                data-testid="profile-address2"
                name="address2"
                value={watch('address2')}
                onChange={(e: any) => {
                  if (errors.address2) clearErrors('address2');
                  setValue('address2', e.target.value, { shouldValidate: true });
                }}
                inputClass={`${styles.profileInputClass} `}
                wrapperClassName={`fs-unmask ${styles.completeProfileAddress2}`}
              />
            </div>
          </div>
          <div className={`${styles.inputWrapRow} ${styles.inputWrapEqualRow}`}>
            <div className={styles.inputWrap}>
              <TextField
                {...register('city')}
                label="City"
                errors={errors.city ? errors : {}}
                data-testid="profile-city"
                name="city"
                value={watch('city')}
                onChange={(e: any) => {
                  if (errors.city) clearErrors('city');
                  setValue('city', e.target.value, { shouldValidate: true });
                }}
                required
                inputClass={`${styles.profileInputClass} `}
                wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
              />
            </div>
            <div className={styles.inputWrap}>
              <MUISelectInput
                {...register('state')}
                label="State"
                data-testid="profile-state"
                errors={errors.state ? errors : {}}
                name="state"
                onChange={(e: any) => {
                  if (errors.state) clearErrors('state');
                  setValue('state', e);
                }}
                value={watch('state') || ''}
                required
                inputClass={`${styles.profileInputClass} `}
                wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
                options={stateOptions}
              />
            </div>
            <div className={styles.inputWrap}>
              <TextField
                {...register('zip_code')}
                label="Zip code"
                data-testid="profile-zipcode"
                errors={errors.zip_code ? errors : {}}
                name="zip_code"
                value={watch('zip_code')}
                onChange={(e: any) => {
                  if (errors.zip_code) clearErrors('zip_code');
                  setValue('zip_code', e.target.value, { shouldValidate: true });
                }}
                required
                inputClass={`${styles.profileInputClass} `}
                wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
              />
            </div>
          </div>
          {userInfo.role.name === UserRoleName.Patient && organizations?.length > 1 && (
            <ReadOnlyOrganizations
              setSelectedOrgId={setSelectedOrgId}
              selectedOrgId={selectedOrgId}
              organizations={organizations}
              currentOrganization={currentOrganization}
            />
          )}
          <div className={styles.buttonDiv}>
            <Button
              type="submit"
              variant="contained"
              data-testid="profile-save-changes"
              className={`${loading && styles.inActiveBtn} fs-unmask ${styles.formBtn}`}
              style={{ borderRadius: '32px', height: '42px', width: 'auto' }}
              disabled={
                (isEqual(myProfileForm, getValues()) &&
                  isEqual(avatarAction, '') &&
                  currentOrganization?.id === selectedOrgId) ||
                isSavingProfile
              }
            >
              Save changes
            </Button>
            <Button
              variant="text"
              data-testid="profile-reset-password"
              color="error"
              onClick={() => setResetPasswordModal(true)}
              className="fs-unmask"
            >
              Reset password
            </Button>
          </div>
        </form>
        <ValidateAddressModal
          title={showValidationAddressModal.title}
          isOpen={showValidationAddressModal.isOpen}
          recommended={showValidationAddressModal.recommended}
          recommendedAddress={showValidationAddressModal.recommendedAddress}
          whatYouEntered={showValidationAddressModal.whatYouEntered}
          onClose={showValidationAddressModal.onClose}
          onKeepCurrentAddress={showValidationAddressModal.onKeepCurrentAddress}
          onConfirmChange={showValidationAddressModal.onConfirmChange}
        />
        {resetPasswordModal && (
          <ChangePasswordModal
            isOpen={resetPasswordModal}
            onClose={() => setResetPasswordModal(false)}
            email={userInfo?.email || ''}
          />
        )}
      </div>
    );
  };

  useEffect(() => {
    if (!isEditDependent && state?.selectedDependentId && state?.openDependentProfile) {
      roleNavigate('/select-patient', { state: { newDependentId: dependentId } });
    }
  }, [isEditDependent]);

  useEffect(() => {
    if (isReturnToSelectPatient && state?.needToCompleteProfile) {
      roleNavigate('/select-patient', { state: { currentUserId: (thisUser?.id || 0).toString() } });
    }
  }, [isReturnToSelectPatient]);

  useEffect(() => {
    if (state?.addDependentFromSideNav) {
      addDependent();
    }
  }, [state]);

  if (loading) {
    return (
      <div className={styles.ProfileDiv}>
        <Loading fullScreen />
      </div>
    );
  }

  return (
    <div className={styles.ProfileContainer}>
      {!isEditDependent && renderPrimaryAccount()}
      {isEditDependent ? (
        <EditDependent
          setIsEditDependent={setIsEditDependent}
          dependentId={dependentId}
          languageOptions={languageOptions}
          validationResponseId={validationResponseId}
          setValidationResponseId={setValidationResponseId}
          isFromSelectNeeds={false}
          showDeleteDependentConfirmationModal={showDeleteDependentConfirmationModal}
          setShowDeleteDependentConfirmationModal={setShowDeleteDependentConfirmationModal}
          showSnackbar={showSnackbar}
        />
      ) : (
        <ReadOnlyDependent
          onEditDependent={onEditDependent}
          addDependent={addDependent}
          reviewProfile={false}
        />
      )}
      <GenericSnackbar open={snackbar.isOpen} message={snackbar.message} type={snackbar.type} onClose={closeSnackbar} />
    </div>
  );
};

export default Profile;
